import { Image } from '../../../../modules/design-system';
import type { TierBadge } from '../../../../modules/api';
import { formatPrice } from '../../../../modules/currency';

export interface TierCardProps {
  badge: TierBadge;
  onClick?: () => void;
  /**
   * `'auto'` (default) switches compact → tall layout at the `@[900px]`
   * container breakpoint — classic, where the card is visible on every
   * width. `'desktop'` forces the tall layout unconditionally — modern
   * renders tier cards only inside the ≥800px sidebar, where the 900px
   * container query would still serve the compact pill on 800–900px
   * viewports.
   */
  layout?: 'auto' | 'desktop';
}

/** Per-layout class sets. Written out as full literals (no computed
    prefixes) so the Tailwind scanner picks every class up. */
const LAYOUT_CLASSES = {
  auto: {
    card:
      '[border-radius:var(--co-TierCard-borderRadius,16px)] @[900px]:[border-radius:var(--co-TierCard-borderRadius,20px)] ' +
      'h-[52px] w-[121px] @[900px]:h-[100px] @[900px]:w-[119px]',
    text:
      'left-[12px] top-[6px] items-start ' +
      '@[900px]:left-[calc(50%+0.5px)] @[900px]:-translate-x-1/2 ' +
      '@[900px]:top-[8px] @[900px]:items-center ' +
      '@[900px]:w-[40px] @[900px]:text-center',
    name:
      'text-[12px] leading-[18px] ' +
      '@[900px]:text-[13px] @[900px]:leading-[20px] @[900px]:tracking-[-0.1px]',
    price:
      'text-[12px] leading-[18px] ' +
      '@[900px]:text-[15px] @[900px]:leading-[22px] @[900px]:tracking-[-0.3px] ' +
      '@[900px]:min-w-full @[900px]:[width:min-content]',
    image:
      'right-[3px] top-[4px] h-[66px] w-[52px] ' +
      '@[900px]:left-1/2 @[900px]:-translate-x-1/2 @[900px]:right-auto ' +
      '@[900px]:top-[58px] @[900px]:h-[122px] @[900px]:w-[97px]',
  },
  desktop: {
    card:
      '[border-radius:var(--co-TierCard-borderRadius,20px)] h-[100px] w-[119px]',
    text:
      'left-[calc(50%+0.5px)] -translate-x-1/2 top-[8px] items-center w-[40px] text-center',
    name: 'text-[13px] leading-[20px] tracking-[-0.1px]',
    price:
      'text-[15px] leading-[22px] tracking-[-0.3px] min-w-full [width:min-content]',
    image: 'left-1/2 -translate-x-1/2 top-[58px] h-[122px] w-[97px]',
  },
} as const;

/**
 * Tier card — one of the "Starter / Premier / Pro" (or similar) pack
 * variants. Two layouts switched by container-query breakpoint (or the
 * tall one forced via `layout="desktop"`):
 *
 *   < 900px (Figma 31:14345) — 121×52 compact pill
 *     rounded-16, text (name 12/18 + price 12/18 semibold) on the LEFT,
 *     pack image 52×66 on the RIGHT, top-4, clipped bottom.
 *
 *   ≥ 900px (Figma 17:5489) — 119×100 tall card
 *     rounded-20, text (name 13/20 -0.1 + price 15/22 semibold -0.3)
 *     centred top (top-8 for active, top-10 for inactive to compensate
 *     for the 2px border — we use top-8 for both and accept the 2px
 *     visual difference under inactive cards, which matches Figma well
 *     enough). Pack image 97×122 centred horizontally at top-58,
 *     clipped bottom with shadow `0 14 44 rgba(0,0,0,0.45)`.
 *
 * Active state (`badge.isSelf`):
 *   • 2px accent border
 *   • outer glow `0 0 4 rgba(accent,0.3)` composed with base shadow
 *   • inner double glow overlay on a separate `<span>`:
 *     `inset 0 0 7 rgba(accent,0.6), inset 0 5 24 rgba(255,255,255,0.14)`
 */
export function TierCard({ badge, onClick, layout = 'auto' }: TierCardProps) {
  const { name, price, imageUrl, isSelf } = badge;
  const priceAmount = Number(price) || 0;
  const priceDisplay = formatPrice(priceAmount);
  const layoutClasses = LAYOUT_CLASSES[layout];

  const base =
    'relative shrink-0 overflow-hidden cursor-pointer ' +
    `${layoutClasses.card} ` +
    'transition-[box-shadow,border-color,filter] duration-200 ease-out ' +
    'hover:brightness-110';

  // Operator-controlled background gradient (Design Constructor knobs).
  // The active card reads its own `activeGradient*` slots first and falls
  // back to the shared gradient, so themes without the active knobs keep
  // painting both states alike.
  const bgStyle = {
    background: isSelf
      ? 'linear-gradient(' +
        'var(--co-TierCard-gradientDirection, to bottom), ' +
        'var(--co-TierCard-activeGradientFrom, var(--co-TierCard-gradientFrom, rgba(191,191,191,0.13))) 0%, ' +
        'var(--co-TierCard-activeGradientTo, var(--co-TierCard-gradientTo, rgba(0,0,0,0.06))) 100%)'
      : 'linear-gradient(' +
        'var(--co-TierCard-gradientDirection, to bottom), ' +
        'var(--co-TierCard-gradientFrom, rgba(191,191,191,0.13)) 0%, ' +
        'var(--co-TierCard-gradientTo, rgba(0,0,0,0.06)) 100%)',
  } as const;

  const shadowClass = isSelf
    ? '[box-shadow:0_0_4px_0_color-mix(in_srgb,var(--co-TierCard-activeBorder,var(--ui-accent))_30%,transparent),0_1px_2px_0_rgba(25,25,25,0.10)]'
    : '[box-shadow:0_1px_2px_0_rgba(25,25,25,0.10)]';

  // Active state border — width + colour are both knobs; width=0 hides it.
  const activeBorderStyle = isSelf
    ? {
        borderWidth: 'var(--co-TierCard-activeBorderWidth, 2px)',
        borderStyle: 'solid' as const,
        borderColor: 'var(--co-TierCard-activeBorder, var(--ui-accent))',
      }
    : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelf}
      data-co-component="TierCard"
      className={`${base} ${shadowClass}`.trim()}
      style={{ ...bgStyle, ...(activeBorderStyle ?? {}) }}
    >
      {/* Text block — left-aligned on mobile, centred top on desktop.
         Desktop frame mirrors Figma: 40px-wide column at left-[calc(50%+0.5px)],
         items-center allows the text lines to overflow symmetrically. */}
      <div className={`absolute z-10 flex flex-col ${layoutClasses.text}`}>
        <span
          className={`font-normal whitespace-nowrap ${layoutClasses.name}`}
          style={{ color: 'var(--co-TierCard-titleText, #ffffff)' }}
        >
          {name}
        </span>
        <span
          className={`font-semibold whitespace-nowrap ${layoutClasses.price}`}
          style={{ color: 'var(--co-TierCard-priceText, #ffffff)' }}
        >
          {priceDisplay}
        </span>
      </div>

      {/* Pack image — right side on mobile, bottom-centre on desktop.
         Desktop wrapper has the Figma drop-shadow as `box-shadow` so it
         renders against the rectangular wrapper (not the alpha shape). */}
      <Image
        src={imageUrl}
        alt=""
        className={`pointer-events-none absolute rounded-none ${layoutClasses.image}`}
        imgClassName="absolute block h-full w-full max-w-none left-0 top-0 object-contain object-top"
      />

      {/* Active inner double-glow overlay */}
      <span
        aria-hidden
        className={
          'pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-200 ' +
          '[box-shadow:inset_0_0_7px_0_color-mix(in_srgb,var(--co-TierCard-activeBorder,var(--ui-accent))_60%,transparent),inset_0_5px_24px_0_rgba(255,255,255,0.14)] ' +
          (isSelf ? 'opacity-100' : 'opacity-0')
        }
      />
    </button>
  );
}
